import { ticketSalesWindows, ticketSalesWindowValue } from "../utils/ticketSalesWindow";
import TestDrawAccessManager from "../components/TestDrawAccessManager";
import AboutUsEditor from "../components/AboutUsEditor";
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  MenuItem
} from "@mui/material";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { useDispatch, useSelector } from "react-redux";
import { setBranding } from "../features/branding/brandingSlice";
import { adminPageSx, contentCardSx, mobileActionGroupSx, textWrapSx } from "../utils/adminResponsive";
import AdminPageContainer from "../components/AdminPageContainer";

const initialState = {
  privacy_policy: "",
  terms_conditions: "",
  cookie_policy: "",
  support_email: "",
  billing_support_email: "",
  tech_support_email: "",
  support_phone: "",
  footer_brand_name: "Flip Lottery",
  footer_powered_by_text: "Product of Flip Games Limited",
  footer_license_text: "Delta State Licensed • NDPR Compliant",
  footer_copyright_text: "© 2026 Flip Games Limited. All rights reserved.",
  footer_location_text: "Delta State, Nigeria",
  facebook_url: "",
  instagram_url: "",
  facebook_page_id: "",
  instagram_business_account_id: "",
  social_metrics_sync_minutes: 15,
  logo_width: 96,
  logo_height: 96,
  rng_provider_name: "",
  draw_running_speed_ms: 1000,
  winners_list_speed: 500,
  winner_deduction_percent: 5,
  ticket_sales_close_hours_before_draw: 3,
  rng_display_note: "",
  live_draw_drumroll_audio_url: "",
  live_draw_drumroll_public_id: "",
  live_draw_winner_boom_audio_url: "",
  live_draw_winner_boom_public_id: "",
  live_draw_animation_video_url: "",
  live_draw_animation_public_id: "",
  live_draw_pre_draw_video_url: "",
  live_draw_pre_draw_public_id: "",
  live_draw_fireworks_video_url: "",
  live_draw_fireworks_public_id: "",
  live_draw_results_audio_url: "",
  live_draw_results_audio_public_id: "",
  live_draw_announcement_page_1: "Welcome to the FlipLottery Live Draw. Please stay on this page as we prepare to begin today's official draw presentation.",
  live_draw_announcement_page_2: "FlipLottery draws are scheduled regularly. Please check the platform for the official date and time of each upcoming draw.",
  live_draw_announcement_page_3: "Only valid tickets purchased before ticket sales close are eligible for the current draw. Please keep your ticket number available while results are presented.",
  live_draw_announcement_page_4: "Winning names, ticket numbers, and prize amounts will be displayed after the draw presentation. Thank you for participating and good luck to everyone.",
  live_draw_announcement_page_5: "",
  announcement_total_duration_seconds: 60,
  shuffle_presentation_duration_seconds: 60,
  is_frontend_coming_soon: false,
  maintenance_notice: "",
  minimum_age_notice: "18+ Only"
};

const liveDrawMediaConfig = {
  pre_draw_video: {
    title: "Pre-Show Background Video",
    description: "Loops for Presenter and public viewers as soon as the assigned Presenter logs in.",
    accept: "video/*",
    urlField: "live_draw_pre_draw_video_url",
    publicIdField: "live_draw_pre_draw_public_id",
    inputLabel: "Choose pre-draw waiting video",
    uploadLabel: "Upload pre-draw video",
    kind: "video"
  },
  drumroll_audio: {
    title: "Drum Roll",
    description: "Plays while the RNG sampling is running on the live draw presentation screen.",
    accept: "audio/*",
    urlField: "live_draw_drumroll_audio_url",
    publicIdField: "live_draw_drumroll_public_id",
    inputLabel: "Choose drum roll soundtrack",
    uploadLabel: "Upload drum roll",
    kind: "audio"
  },
  winner_boom_audio: {
    title: "Winner Boom",
    description: "Plays once just before winners are revealed, alongside the fireworks sequence.",
    accept: "audio/*",
    urlField: "live_draw_winner_boom_audio_url",
    publicIdField: "live_draw_winner_boom_public_id",
    inputLabel: "Choose boom / gunshot audio",
    uploadLabel: "Upload winner boom",
    kind: "audio"
  },
  draw_animation_video: {
    title: "Draw Presentation Video",
    description:
      "Plays instead of the flashing ticket animation after a draw starts. If the video is unavailable, the flashing ticket animation is used automatically.",
    accept: "video/*",
    urlField: "live_draw_animation_video_url",
    publicIdField: "live_draw_animation_public_id",
    inputLabel: "Choose draw animation video",
    uploadLabel: "Upload animation video",
    kind: "video"
  },
  fireworks_video: {
    title: "Post-Draw Celebration Video",
    description: "Existing celebration/dancing clip shown after finalization and before the winner cast.",
    accept: "video/*",
    urlField: "live_draw_fireworks_video_url",
    publicIdField: "live_draw_fireworks_public_id",
    inputLabel: "Choose post-draw celebration video",
    uploadLabel: "Upload celebration video",
    kind: "video"
  },
  results_audio: {
    title: "Winner Results Background Audio",
    description: "Loops from Cloudinary/CDN while the official winner cast is displayed.",
    accept: "audio/*",
    urlField: "live_draw_results_audio_url",
    publicIdField: "live_draw_results_audio_public_id",
    inputLabel: "Choose winner results background audio",
    uploadLabel: "Upload results audio",
    kind: "audio"
  }
};

const settingsFieldGridSx = {
  display: "grid",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))"
  },
  gap: 2
};

const settingsFieldSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  "& .MuiInputBase-root": {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

const settingsSectionCardSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  boxSizing: "border-box"
};

const initialRumbleStatus = {
  enabled: false,
  configured: false,
  connected: false,
  status: "UNKNOWN",
  isLive: false,
  title: "",
  watchingNow: 0,
  playerAvailable: false,
  checkedAt: null,
  stale: false
};

function getRumbleConnectionDisplay(rumble = initialRumbleStatus) {
  if (!rumble.enabled) {
    return { label: "Disabled", color: "default" };
  }

  if (!rumble.configured) {
    return { label: "Not Configured", color: "warning" };
  }

  if (rumble.status === "ERROR") {
    return { label: "API Error", color: "error" };
  }

  if (rumble.connected) {
    return { label: "Connected", color: "success" };
  }

  return { label: "Checking", color: "default" };
}

function getRumbleStreamDisplay(rumble = initialRumbleStatus) {
  if (rumble.status === "LIVE" && rumble.isLive) {
    return { label: "Live", color: "error" };
  }

  if (rumble.status === "OFFLINE") {
    return { label: "Offline", color: "default" };
  }

  return { label: "Unknown", color: "default" };
}

export default function Settings() {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(initialState);
  const [activeMediaTab, setActiveMediaTab] = useState("drumroll_audio");
  const [mediaFiles, setMediaFiles] = useState({
    drumroll_audio: null,
    winner_boom_audio: null,
    draw_animation_video: null,
    fireworks_video: null,
    results_audio: null
  });
  const [uploadingMedia, setUploadingMedia] = useState({
    drumroll_audio: false,
    winner_boom_audio: false,
    draw_animation_video: false,
    fireworks_video: false,
    results_audio: false
  });
  const [deletingMedia, setDeletingMedia] = useState({
    drumroll_audio: false,
    winner_boom_audio: false,
    draw_animation_video: false,
    fireworks_video: false,
    results_audio: false
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rumbleStatus, setRumbleStatus] = useState(initialRumbleStatus);
  const [testingRumble, setTestingRumble] = useState(false);
  const adminState = useSelector((state) => state.auth?.admin);
  const adminRole =
    typeof adminState === "string"
      ? adminState
      : adminState?.role ||
        adminState?.adminRole ||
        adminState?.name ||
        adminState?.admin_name ||
        "";
  const isSuperAdmin = String(adminRole || "").trim().toLowerCase() === "super_admin";
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const submitSettings = async (payloadData = formData) => {
    try {
      const payload = {
        ...payloadData,
        is_frontend_coming_soon: Boolean(payloadData.is_frontend_coming_soon) ? 1 : 0
      };

      const data = await adminFetch("/api/admin/settings", {
        method: "PUT",
        body: JSON.stringify(payload)
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to update settings", "error");
        return false;
      }

      setFormData((prev) => ({
        ...prev,
        ...payloadData,
        is_frontend_coming_soon: Boolean(payloadData.is_frontend_coming_soon)
      }));

      dispatch(
        setBranding({
          logoUrl: logoPreview,
          logoWidth: formData.logo_width,
          logoHeight: formData.logo_height
        })
      );

      showSnackbar("Settings updated successfully");
      return true;
    } catch (error) {
      console.log(error);
      showSnackbar("Server error", "error");
      return false;
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);

      try {
        const data = await adminFetch("/api/admin/settings", {
          method: "GET"
        });

        if (data?.status && data.settings) {
          if (data.internal_config?.rumble_live_stream) {
            setRumbleStatus({
              ...initialRumbleStatus,
              ...data.internal_config.rumble_live_stream
            });
          }

          dispatch(
            setBranding({
              logoUrl: data.settings.logo_url || "",
              logoWidth: data.settings.logo_width || 96,
              logoHeight: data.settings.logo_height || 96
            })
          );
          setFormData({
            privacy_policy: data.settings.privacy_policy || "",
            terms_conditions: data.settings.terms_conditions || "",
            cookie_policy: data.settings.cookie_policy || "",
            support_email: data.settings.support_email || "",
            billing_support_email: data.settings.billing_support_email || "",
            tech_support_email: data.settings.tech_support_email || "",
            support_phone: data.settings.support_phone || "",
            footer_brand_name: data.settings.footer_brand_name || "Flip Lottery",
            footer_powered_by_text: data.settings.footer_powered_by_text || "Product of Flip Games Limited",
            footer_license_text: data.settings.footer_license_text || "Delta State Licensed • NDPR Compliant",
            footer_copyright_text: data.settings.footer_copyright_text || "© 2026 Flip Games Limited. All rights reserved.",
            footer_location_text: data.settings.footer_location_text || "Delta State, Nigeria",
            facebook_url: data.settings.facebook_url || "",
            instagram_url: data.settings.instagram_url || "",
            facebook_page_id: data.settings.facebook_page_id || "",
            instagram_business_account_id: data.settings.instagram_business_account_id || "",
            social_metrics_sync_minutes: data.settings.social_metrics_sync_minutes ?? 15,
            logo_width: data.settings.logo_width || 96,
            logo_height: data.settings.logo_height || 96,
            rng_provider_name: data.settings.rng_provider_name || "",
            draw_running_speed_ms: data.settings.draw_running_speed_ms || 1000,
            winners_list_speed: Number(data.settings.winners_list_speed ?? 500),
            winner_deduction_percent: data.settings.winner_deduction_percent ?? 5,
            ticket_sales_close_hours_before_draw: Number(data.settings.ticket_sales_close_hours_before_draw || 3),
            rng_display_note: data.settings.rng_display_note || "",
            live_draw_drumroll_audio_url: data.settings.live_draw_drumroll_audio_url || "",
            live_draw_drumroll_public_id: data.settings.live_draw_drumroll_public_id || "",
            live_draw_winner_boom_audio_url: data.settings.live_draw_winner_boom_audio_url || "",
            live_draw_winner_boom_public_id: data.settings.live_draw_winner_boom_public_id || "",
            live_draw_animation_video_url: data.settings.live_draw_animation_video_url || "",
            live_draw_pre_draw_video_url: data.settings.live_draw_pre_draw_video_url || "",
            live_draw_pre_draw_public_id: data.settings.live_draw_pre_draw_public_id || "",
            live_draw_animation_public_id: data.settings.live_draw_animation_public_id || "",
            live_draw_fireworks_video_url: data.settings.live_draw_fireworks_video_url || "",
            live_draw_results_audio_url: data.settings.live_draw_results_audio_url || "",
            live_draw_results_audio_public_id: data.settings.live_draw_results_audio_public_id || "",
            live_draw_announcement_page_1: data.settings.live_draw_announcement_page_1 ?? initialState.live_draw_announcement_page_1,
            live_draw_announcement_page_2: data.settings.live_draw_announcement_page_2 ?? initialState.live_draw_announcement_page_2,
            live_draw_announcement_page_3: data.settings.live_draw_announcement_page_3 ?? initialState.live_draw_announcement_page_3,
            live_draw_announcement_page_4: data.settings.live_draw_announcement_page_4 ?? initialState.live_draw_announcement_page_4,
            live_draw_announcement_page_5: data.settings.live_draw_announcement_page_5 ?? initialState.live_draw_announcement_page_5,
            announcement_total_duration_seconds: Number(data.settings.announcement_total_duration_seconds || 60),
            shuffle_presentation_duration_seconds: Number(data.settings.shuffle_presentation_duration_seconds || 60),
            live_draw_fireworks_public_id: data.settings.live_draw_fireworks_public_id || "",
            is_frontend_coming_soon: Boolean(data.settings.is_frontend_coming_soon),
            maintenance_notice: data.settings.maintenance_notice || "",
            minimum_age_notice: data.settings.minimum_age_notice || "18+ Only"
          });
          setLogoPreview(data.settings.logo_url || "");
        }
      } catch (error) {
        console.log(error);
      }

      setLoading(false);
    };

    loadSettings();
  }, [dispatch]);

  useEffect(() => {
    let active = true;

    const refreshRumbleStatus = async () => {
      const data = await adminFetch("/api/admin/settings/rumble/status", {
        method: "GET"
      });

      if (active && data?.status && data.rumble) {
        setRumbleStatus({
          ...initialRumbleStatus,
          ...data.rumble
        });
      }
    };

    refreshRumbleStatus();
    const interval = window.setInterval(refreshRumbleStatus, 15000);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    await submitSettings();
    setSaving(false);
  };

  const handleToggleComingSoon = async () => {
    if (!isSuperAdmin) {
      showSnackbar("Only super admins can change coming soon mode.", "error");
      return;
    }

    setSaving(true);

    const nextValue = !Boolean(formData.is_frontend_coming_soon);
    await submitSettings({
      ...formData,
      is_frontend_coming_soon: nextValue
    });

    setSaving(false);
  };

  const handleTestRumbleConnection = async () => {
    setTestingRumble(true);

    try {
      const data = await adminFetch("/api/admin/settings/rumble/test", {
        method: "POST"
      });

      if (data?.rumble) {
        setRumbleStatus({
          ...initialRumbleStatus,
          ...data.rumble
        });
      }

      showSnackbar(
        data?.message || "Unable to test the Rumble connection",
        data?.status ? "success" : "error"
      );
    } catch (_error) {
      showSnackbar("Unable to test the Rumble connection", "error");
    }

    setTestingRumble(false);
  };

  const handleLogoFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      showSnackbar("Select a logo image first", "error");
      return;
    }

    setUploadingLogo(true);

    try {
      const payload = new FormData();
      payload.append("logo", logoFile);

      const data = await adminFetch("/api/admin/settings/logo", {
        method: "POST",
        body: payload
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to upload logo", "error");
        setUploadingLogo(false);
        return;
      }

      const nextLogoUrl = data.logo?.logo_url || "";
      setLogoPreview(nextLogoUrl);
      setLogoFile(null);
      dispatch(
        setBranding({
          logoUrl: nextLogoUrl,
          logoWidth: formData.logo_width,
          logoHeight: formData.logo_height
        })
      );
      showSnackbar("Logo updated successfully");
    } catch (error) {
      console.log(error);
      showSnackbar("Failed to upload logo", "error");
    }

    setUploadingLogo(false);
  };

  const handleMediaFileChange = (assetKey, event) => {
    const file = event.target.files?.[0] || null;

    setMediaFiles((prev) => ({
      ...prev,
      [assetKey]: file
    }));
  };

  const handleLiveDrawMediaUpload = async (assetKey) => {
    const file = mediaFiles[assetKey];
    const mediaConfig = liveDrawMediaConfig[assetKey];

    if (!file || !mediaConfig) {
      showSnackbar("Choose a media file first", "error");
      return;
    }

    setUploadingMedia((prev) => ({
      ...prev,
      [assetKey]: true
    }));

    try {
      const payload = new FormData();
      payload.append("file", file);

      const data = await adminFetch(`/api/admin/settings/live-draw-media/${assetKey}`, {
        method: "POST",
        body: payload
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to upload live draw media", "error");
        setUploadingMedia((prev) => ({
          ...prev,
          [assetKey]: false
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [mediaConfig.urlField]: data.media?.url || "",
        [mediaConfig.publicIdField]: data.media?.public_id || ""
      }));
      setMediaFiles((prev) => ({
        ...prev,
        [assetKey]: null
      }));
      showSnackbar(data?.message || "Live draw media uploaded successfully");
    } catch (error) {
      console.log(error);
      showSnackbar("Failed to upload live draw media", "error");
    }

    setUploadingMedia((prev) => ({
      ...prev,
      [assetKey]: false
    }));
  };

  const handleLiveDrawMediaDelete = async (assetKey) => {
    const mediaConfig = liveDrawMediaConfig[assetKey];

    if (!mediaConfig || !formData[mediaConfig.urlField]) {
      showSnackbar("No uploaded media found for this tab", "error");
      return;
    }

    setDeletingMedia((prev) => ({
      ...prev,
      [assetKey]: true
    }));

    try {
      const data = await adminFetch(`/api/admin/settings/live-draw-media/${assetKey}`, {
        method: "DELETE"
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to remove live draw media", "error");
        setDeletingMedia((prev) => ({
          ...prev,
          [assetKey]: false
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [mediaConfig.urlField]: "",
        [mediaConfig.publicIdField]: ""
      }));
      setMediaFiles((prev) => ({
        ...prev,
        [assetKey]: null
      }));
      showSnackbar(data?.message || "Live draw media removed successfully");
    } catch (error) {
      console.log(error);
      showSnackbar("Failed to remove live draw media", "error");
    }

    setDeletingMedia((prev) => ({
      ...prev,
      [assetKey]: false
    }));
  };

  const currentMediaConfig = liveDrawMediaConfig[activeMediaTab];
  const currentMediaUrl = formData[currentMediaConfig.urlField];

  return (
    <Box
      sx={{
        ...adminPageSx,
        overflowX: "hidden"
      }}
    >
      <AdminPageContainer>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
          Platform Settings
        </Typography>

        <TestDrawAccessManager canEdit={isSuperAdmin} />
        <AboutUsEditor canEdit={isSuperAdmin} />

        <Card
          sx={{
            p: { xs: 2, sm: 3, lg: 4 },
            maxWidth: 1200,
            borderRadius: 3,
            ...contentCardSx,
            width: "100%",
            boxSizing: "border-box",
            "& .MuiTextField-root": settingsFieldSx
          }}
        >
          {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight={700}>
              Logo Settings
            </Typography>

            <Card variant="outlined" sx={{ ...settingsSectionCardSx, p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
                  Upload or replace the application logo used across the platform.
                </Typography>

                <Box sx={settingsFieldGridSx}>
                  <TextField
                    label="Logo Width (px)"
                    type="number"
                    value={formData.logo_width}
                    onChange={(e) => updateField("logo_width", e.target.value)}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />

                  <TextField
                    label="Logo Height (px)"
                    type="number"
                    value={formData.logo_height}
                    onChange={(e) => updateField("logo_height", e.target.value)}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                </Box>

                <Box
                  sx={{
                    width: Number(formData.logo_width) || 120,
                    height: Number(formData.logo_height) || 120,
                    maxWidth: "100%",
                    maxHeight: 240,
                    borderRadius: 2,
                    border: "1px solid rgba(0,0,0,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    bgcolor: "rgba(0,0,0,0.02)"
                  }}
                >
                  {logoPreview ? (
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="App logo preview"
                      sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No logo uploaded
                    </Typography>
                  )}
                </Box>

                <Box sx={mobileActionGroupSx}>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Choose Logo
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                    />
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleLogoUpload}
                    disabled={!logoFile || uploadingLogo}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    {uploadingLogo ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Upload Logo"}
                  </Button>
                </Box>
              </Stack>
            </Card>

            <Typography variant="h6" fontWeight={700}>
              Support Contacts
            </Typography>

            <Box sx={settingsFieldGridSx}>
              <TextField
                label="Support Email"
                value={formData.support_email}
                onChange={(e) => updateField("support_email", e.target.value)}
                fullWidth
              />
              <TextField
                label="Billing Support Email"
                value={formData.billing_support_email}
                onChange={(e) => updateField("billing_support_email", e.target.value)}
                fullWidth
              />
            </Box>

            <Box sx={settingsFieldGridSx}>
              <TextField
                label="Technical Support Email"
                value={formData.tech_support_email}
                onChange={(e) => updateField("tech_support_email", e.target.value)}
                fullWidth
              />
              <TextField
                label="Support Phone"
                value={formData.support_phone}
                onChange={(e) => updateField("support_phone", e.target.value)}
                fullWidth
              />
            </Box>

            <Typography variant="h6" fontWeight={700}>
              Footer Content
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
              These details appear in the public footer and can be updated whenever your licensing or location changes.
            </Typography>

            <Box sx={settingsFieldGridSx}>
              <TextField
                label="Footer Brand Name"
                value={formData.footer_brand_name}
                onChange={(e) => updateField("footer_brand_name", e.target.value)}
                fullWidth
              />
              <TextField
                label="Powered By Text"
                value={formData.footer_powered_by_text}
                onChange={(e) => updateField("footer_powered_by_text", e.target.value)}
                fullWidth
              />
            </Box>

            <Box sx={settingsFieldGridSx}>
              <TextField
                label="License Text"
                value={formData.footer_license_text}
                onChange={(e) => updateField("footer_license_text", e.target.value)}
                fullWidth
              />
              <TextField
                label="Location Text"
                value={formData.footer_location_text}
                onChange={(e) => updateField("footer_location_text", e.target.value)}
                fullWidth
              />
            </Box>

            <TextField
              label="Copyright Text"
              value={formData.footer_copyright_text}
              onChange={(e) => updateField("footer_copyright_text", e.target.value)}
              fullWidth
            />

            <Typography variant="h6" fontWeight={700}>
              Social Media Handles
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
              These links will display in the public footer beside the company contact details.
            </Typography>

            <Box sx={settingsFieldGridSx}>
              <TextField
                label="Facebook URL"
                value={formData.facebook_url}
                onChange={(e) => updateField("facebook_url", e.target.value)}
                placeholder="https://facebook.com/fliplottery"
                fullWidth
              />
              <TextField
                label="Instagram URL"
                value={formData.instagram_url}
                onChange={(e) => updateField("instagram_url", e.target.value)}
                placeholder="https://instagram.com/fliplottery"
                fullWidth
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
              These account IDs are used by the admin social metrics sync. Keep API keys and access tokens on the server as environment variables.
            </Typography>

            <Box sx={settingsFieldGridSx}>
              <TextField
                label="Facebook Page ID / Username"
                value={formData.facebook_page_id}
                onChange={(e) => updateField("facebook_page_id", e.target.value)}
                placeholder="fliplottery or 1234567890"
                fullWidth
              />
              <TextField
                label="Instagram Business Account ID"
                value={formData.instagram_business_account_id}
                onChange={(e) => updateField("instagram_business_account_id", e.target.value)}
                placeholder="17841400000000000"
                fullWidth
              />
            </Box>

            <TextField
              label="Social Metrics Sync Interval (minutes)"
              type="number"
              value={formData.social_metrics_sync_minutes}
              onChange={(e) => updateField("social_metrics_sync_minutes", e.target.value)}
              fullWidth
              inputProps={{ min: 0 }}
              helperText="Set to 0 to disable automatic syncing. Manual sync remains available in admin."
            />

            <Typography variant="h6" fontWeight={700}>
              Rumble Live Streaming
            </Typography>

            <Card variant="outlined" sx={{ ...settingsSectionCardSx, p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <Chip
                    label={getRumbleConnectionDisplay(rumbleStatus).label}
                    color={getRumbleConnectionDisplay(rumbleStatus).color}
                    variant="outlined"
                  />
                  <Chip
                    label={getRumbleStreamDisplay(rumbleStatus).label}
                    color={getRumbleStreamDisplay(rumbleStatus).color}
                  />
                  {rumbleStatus.stale ? (
                    <Chip label="Last known status" color="warning" variant="outlined" />
                  ) : null}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
                  The backend checks the official FlipLottery Rumble account and automatically
                  updates the public Live Draw page. The private Rumble API URL is stored only in
                  the backend environment and is never displayed here.
                </Typography>

                {rumbleStatus.status === "LIVE" ? (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={textWrapSx}>
                      Current stream: {rumbleStatus.title || "FlipLottery Live Draw"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Watching now: {Number(rumbleStatus.watchingNow || 0).toLocaleString()}
                    </Typography>
                  </Box>
                ) : null}

                {rumbleStatus.status === "LIVE" && !rumbleStatus.playerAvailable ? (
                  <Alert severity="warning" sx={textWrapSx}>
                    Rumble is live, but no supported player URL was returned or configured. Add an
                    official Rumble embed fallback in the backend environment, or keep the existing
                    draw-specific embed URL configured.
                  </Alert>
                ) : null}

                {rumbleStatus.status === "ERROR" ? (
                  <Alert severity="error" sx={textWrapSx}>
                    The Rumble status API is temporarily unavailable or not configured correctly.
                    The draw system remains available.
                  </Alert>
                ) : null}

                <Box sx={mobileActionGroupSx}>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleTestRumbleConnection}
                    disabled={testingRumble || !isSuperAdmin}
                  >
                    {testingRumble ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Test Rumble Connection"
                    )}
                  </Button>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={textWrapSx}>
                  Configure RUMBLE_LIVE_STREAM_API_URL and RUMBLE_LIVE_STREAM_ENABLED on the backend
                  deployment. The OBS static stream key stays in OBS or Rumble Studio.
                </Typography>
              </Stack>
            </Card>

            <Typography variant="h6" fontWeight={700}>
              Ticket Sales Settings
            </Typography>

            <TextField
              select
              label="Ticket Sales Close Before Draw"
              value={ticketSalesWindowValue(formData.ticket_sales_close_hours_before_draw)}
              onChange={(e) => updateField("ticket_sales_close_hours_before_draw", Number(e.target.value))}
              fullWidth
              helperText="Default for newly created draws. Edit an existing draw explicitly to change its deadline."
            >
              {ticketSalesWindows.map(({value,label}) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
            </TextField>

            <Typography variant="h6" fontWeight={700}>
              RNG Settings
            </Typography>

            <Box sx={settingsFieldGridSx}>
              <TextField
                label="RNG Provider Name"
                value={formData.rng_provider_name}
                onChange={(e) => updateField("rng_provider_name", e.target.value)}
                fullWidth
              />
              <TextField
                label="Draw Running Speed (ms)"
                type="number"
                value={formData.draw_running_speed_ms}
                onChange={(e) => updateField("draw_running_speed_ms", e.target.value)}
                fullWidth
              />
            </Box>

            <TextField
              label="winnersListSpeed (m/s)"
              type="number"
              value={formData.winners_list_speed}
              onChange={(e) => updateField("winners_list_speed", e.target.value)}
              fullWidth
              inputProps={{ min: 1, max: 500, step: 1 }}
              helperText="Controls how fast the winners list scrolls on the live draw winners screen. Higher values scroll faster. Default is 500 m/s."
            />

            <TextField
              label="Withholding Tax (%)"
              type="number"
              value={formData.winner_deduction_percent}
              onChange={(e) => updateField("winner_deduction_percent", e.target.value)}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              helperText="This percentage is deducted as withholding tax from each winner's prize before the net amount is credited to their wallet."
            />

            <TextField
              label="RNG Display Note"
              value={formData.rng_display_note}
              onChange={(e) => updateField("rng_display_note", e.target.value)}
              fullWidth
              multiline
              rows={3}
            />

            <Typography variant="h6" fontWeight={700}>
              Live Draw Announcements
            </Typography>

            {[1, 2, 3, 4, 5].map((page) => (
              <TextField key={page} label={`Announcement Page ${page}`} value={formData[`live_draw_announcement_page_${page}`]}
                onChange={(event) => updateField(`live_draw_announcement_page_${page}`, event.target.value)} multiline rows={4} fullWidth />
            ))}

            <TextField select label="Total Announcement Presentation Time" value={formData.announcement_total_duration_seconds}
              onChange={(event) => updateField("announcement_total_duration_seconds", Number(event.target.value))} fullWidth>
              {[60, 120, 180, 300, 600, 900, 1200].map((seconds) => (
                <MenuItem key={seconds} value={seconds}>{seconds / 60} {seconds === 60 ? "minute" : "minutes"}</MenuItem>
              ))}
            </TextField>

            <TextField select label="Ticket Shuffle / Drum Presentation Duration" value={formData.shuffle_presentation_duration_seconds}
              onChange={(event) => updateField("shuffle_presentation_duration_seconds", Number(event.target.value))} fullWidth>
              <MenuItem value={30}>30 seconds</MenuItem>
              {Array.from({ length: 20 }, (_, index) => (index + 1) * 60).map((seconds) => (
                <MenuItem key={seconds} value={seconds}>{seconds / 60} {seconds === 60 ? "minute" : "minutes"}</MenuItem>
              ))}
            </TextField>

            <Typography variant="h6" fontWeight={700}>
              Live Draw Media
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
              Upload the pre-draw waiting video and presentation assets used directly on FlipLottery during the live draw and winner announcement.
            </Typography>

            <Alert severity="info" sx={textWrapSx}>
              Videos are uploaded to Cloudinary and delivered directly from its CDN. The backend stores only their URLs and metadata.
            </Alert>

            <Alert severity="warning" sx={textWrapSx}>
              Upload requirements: super admin access, maximum file size of 100MB, audio files for drum roll and winner boom, and video files for the optional draw animation and fireworks reveal.
            </Alert>

            <Card variant="outlined" sx={{ ...settingsSectionCardSx, p: { xs: 2, sm: 3 } }}>
              <Stack spacing={3}>
                <Tabs
                  value={activeMediaTab}
                  onChange={(_, value) => setActiveMediaTab(value)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 48,
                    width: "100%",
                    "& .MuiTab-root": {
                      minHeight: 40,
                      textTransform: "none",
                      minWidth: 0,
                      textAlign: "left",
                      whiteSpace: "normal",
                      fontWeight: 700
                    }
                  }}
                >
                  {Object.entries(liveDrawMediaConfig).map(([key, config]) => (
                    <Tab key={key} value={key} label={config.title} />
                  ))}
                </Tabs>

                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {currentMediaConfig.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentMediaConfig.description}
                    </Typography>
                  </Box>

                  <Box sx={mobileActionGroupSx}>
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      {currentMediaConfig.inputLabel}
                      <input
                        hidden
                        type="file"
                        accept={currentMediaConfig.accept}
                        onChange={(event) => handleMediaFileChange(activeMediaTab, event)}
                      />
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => handleLiveDrawMediaUpload(activeMediaTab)}
                      disabled={
                        !mediaFiles[activeMediaTab] ||
                        uploadingMedia[activeMediaTab] ||
                        deletingMedia[activeMediaTab]
                      }
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      {uploadingMedia[activeMediaTab] ? (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                      ) : (
                        currentMediaConfig.uploadLabel
                      )}
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleLiveDrawMediaDelete(activeMediaTab)}
                      disabled={!currentMediaUrl || deletingMedia[activeMediaTab] || uploadingMedia[activeMediaTab]}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      {deletingMedia[activeMediaTab] ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        currentMediaConfig.kind === "video" ? "Delete video" : "Delete audio"
                      )}
                    </Button>
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    {mediaFiles[activeMediaTab]
                      ? `Pending upload: ${mediaFiles[activeMediaTab].name}`
                      : "No new file selected yet."}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Uploading a new file for this tab will overwrite the current global asset used across all draws.
                  </Typography>

                  <Card
                    variant="outlined"
                    sx={{
                      ...settingsSectionCardSx,
                      p: 2,
                      bgcolor: "rgba(0,0,0,0.02)",
                      borderStyle: "dashed"
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                        Current asset URL: {currentMediaUrl || "Not uploaded yet"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                        Public ID: {formData[currentMediaConfig.publicIdField] || "Not available"}
                      </Typography>

                      {currentMediaUrl ? (
                        currentMediaConfig.kind === "audio" ? (
                          <Box
                            component="audio"
                            controls
                            preload="metadata"
                            src={currentMediaUrl}
                            sx={{ width: "100%" }}
                          />
                        ) : (
                          <Box
                            component="video"
                            controls
                            preload="metadata"
                            src={currentMediaUrl}
                            sx={{
                              width: "100%",
                              maxHeight: 320,
                              borderRadius: 2,
                              bgcolor: "#000"
                            }}
                          />
                        )
                      ) : null}
                    </Stack>
                  </Card>
                </Stack>
              </Stack>
            </Card>

            <Typography variant="h6" fontWeight={700}>
              Legal Content
            </Typography>

            <TextField
              label="Privacy Policy"
              value={formData.privacy_policy}
              onChange={(e) => updateField("privacy_policy", e.target.value)}
              fullWidth
              multiline
              rows={8}
            />

            <TextField
              label="Terms and Conditions"
              value={formData.terms_conditions}
              onChange={(e) => updateField("terms_conditions", e.target.value)}
              fullWidth
              multiline
              rows={8}
            />

            <TextField
              label="Cookies Policy"
              value={formData.cookie_policy}
              onChange={(e) => updateField("cookie_policy", e.target.value)}
              fullWidth
              multiline
              rows={6}
            />

            <Typography variant="h6" fontWeight={700}>
              Additional Settings
            </Typography>

            <TextField
              label="Maintenance Notice"
              value={formData.maintenance_notice}
              onChange={(e) => updateField("maintenance_notice", e.target.value)}
              fullWidth
              multiline
              rows={3}
            />

            <TextField
              label="Minimum Age Notice"
              value={formData.minimum_age_notice}
              onChange={(e) => updateField("minimum_age_notice", e.target.value)}
              fullWidth
            />

            <Card variant="outlined" sx={{ ...settingsSectionCardSx, p: 3, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                  Frontend Access Control
                </Typography>

                <Box sx={mobileActionGroupSx}>
                  <Button
                    variant={formData.is_frontend_coming_soon ? "outlined" : "contained"}
                    color="warning"
                    onClick={handleToggleComingSoon}
                    disabled={saving || !isSuperAdmin}
                  >
                    {saving
                      ? "Applying..."
                      : formData.is_frontend_coming_soon
                        ? "Disable Coming Soon"
                        : "Enable Coming Soon"}
                  </Button>

                  {!isSuperAdmin ? (
                    <Alert severity="error" sx={{ py: 0 }}>
                      Only super admins can update this mode.
                    </Alert>
                  ) : null}
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {formData.is_frontend_coming_soon
                    ? "Frontend users are blocked and will see the Coming Soon screen."
                    : "Frontend users can access the app normally."}
                </Typography>

                {!formData.is_frontend_coming_soon ? null : (
                  <Alert severity="warning">
                    Set the Maintenance Notice below to control what users see on the Coming Soon screen.
                  </Alert>
                )}
              </Stack>
            </Card>

            <Button
              variant="contained"
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-start" },
                bgcolor: Colors.accent,
                color: "#003300",
                fontWeight: 700
              }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Save Settings"}
            </Button>
          </Stack>
        )}
      </Card>
      </AdminPageContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
